<template>
	<main>

		<div v-if="!hasAddress">
			<eia-map></eia-map>
		</div>

		<div v-else-if="hasSiteArea">
			<eia-form-group id="yes">
				<template slot="label">Yes</template>
				<eia-checkbox
					id="yes"
					type="radio"
					name="calculatedSiteAreaIsCorrect"
					:value="calculatedSiteAreaIsCorrectYes"
					@input="onSiteAreaCorrectInput">
				</eia-checkbox>
			</eia-form-group>

			<eia-form-group id="no">
				<template slot="label">No</template>
				<eia-checkbox
					type="radio"
					id="no"
					name="calculatedSiteAreaIsCorrect"
					:value="calculatedSiteAreaIsCorrectNo"
					@input="onSiteAreaCorrectInput">
				</eia-checkbox>
			</eia-form-group>

			<div v-if="calculatedSiteAreaIsCorrectNo"
				class="eia-indented">
				<div>
					<eia-form-group
						class="eia-stage-four__manual-site-area"
						id="manual-site-area"
						variation="stacked">
						<span slot="label">Roughly, what size is your site in {{ displayUnits }}?</span>

						<eia-validated-input
							id="manual-site-area"
							type="number"
							:validators="[]"
							:show-error-indentation="true"
							placeholder=""
							:value="displaySiteAreaUnifiedValue"
							@input="onManualSiteAreaInput">
						</eia-validated-input>
					</eia-form-group>
					<eia-unit-switcher @switch="switchDisplayUnits"></eia-unit-switcher>
				</div>
			</div>
		</div>

		<div v-else>
			<eia-form-group
				class="eia-stage-four__manual-site-area"
				id="manual-site-area"
				variation="stacked">
				<span slot="label">Roughly, what size is your site in {{ displayUnits }}?</span>

				<eia-validated-input
					id="manual-site-area"
					type="number"
					:validators="[]"
					:show-error-indentation="true"
					placeholder="E.g. 250"
					:value="displaySiteAreaUnifiedValue"
					@input="onManualSiteAreaInput">
				</eia-validated-input>
			</eia-form-group>

			<eia-unit-switcher @switch="switchDisplayUnits"></eia-unit-switcher>
		</div>
	</main>
</template>

<script>
import { mapMutations, mapGetters, mapState } from 'vuex';
import { get } from '@/utils/api-utils';
import centroid from '@turf/centroid';
import { getCoord } from '@turf/invariant';
import { to } from '@/utils/promise-utils';
import { units, convertWithUnits } from '@/utils/math-utils';
import { numberToDisplayFormat } from '@/utils/string-utils';
import { matchAnswer } from '@/utils';

export default {
	name: 'eia-area-map-question',

	props: {
		question: {
			type: Object,
			default: null,
		},
	},

	data(){
		return {
			hasAddress: false,
			hasSiteArea: null,
			displaySiteAreaUnifiedValue: 0,
		};
	},

	beforeMount(){
		this.setCurrentStage();
		this.onStatusChange();

		// check for unit being used and switch
		for( const a of this.question.answers ){

			if( a.unit ){
				this.$store.commit( 'stages/switchAreaDisplayUnits', a.unit );
				break;
			}
		}

	},

	computed: {

		...mapState( 'stages', [ 'manualAddress', 'siteAreaDisplayUnits' ] ),
		...mapGetters( 'stages', [
			'geoJSONBoundary',
			'manualAddressValid',
			'manualAddressArray',
			'calculatedSiteAreaIsCorrect',
			'siteArea',
			'siteAreaDisplayUnits',
			'manualSiteArea',
			'displaySiteAreaUnified',
			'displaySiteArea',
		] ),

		calculatedSiteAreaIsCorrectYes() {
			return this.calculatedSiteAreaIsCorrect === true;
		},

		calculatedSiteAreaIsCorrectNo() {
			return this.calculatedSiteAreaIsCorrect === false;
		},

		displayUnits() {
			return this.siteAreaDisplayUnits.displayLongName;
		},

		switchDisplayUnitsMessage() {
			if( this.siteAreaDisplayUnits.id === units.ACRES.id ) {
				return `Change to ${units.HECTARES.displayLongName}`;
			}

			return `Change to ${units.ACRES.displayLongName}`;
		},

		/*nextButtonDisabled() {
			return !(
				this.calculatedSiteAreaIsCorrect === true ||
				this.manualSiteArea
			);
		},*/

		currentStage(){

			let title = 'Site Location';
			let header = 'Where is your site?';
			let nextButtonLabel = 'Confirm Site Location';
			let subHeader = 'Please draw your site extent';

			if( this.hasAddress ){
				const siteArea = this.displaySiteArea; //store.getters[ 'stages/displaySiteArea' ];
				const siteAreaDisplayUnits = this.$store.getters[ 'stages/siteAreaDisplayUnits' ];

				if( siteArea ) {
					const formatted = numberToDisplayFormat( siteArea );

					header = `We have calculated the area of your site at ${formatted}${siteAreaDisplayUnits.displayShortName} is that correct?`;
					subHeader = '';
				}

				else{
					header = 'What is your site\'s area?';
					subHeader = '';
				}

				title = 'Area of Site';
				nextButtonLabel = undefined;
			}

			return { title, header, subHeader, nextButtonLabel };
		},

	},

	watch: {

		// watch for geoJSONBoundary change from main stages state
		geoJSONBoundary(){
			this.onStatusChange();
		},

		manualAddressValid(){
			this.onStatusChange();
		},

		calculatedSiteAreaIsCorrect(){
			this.onStatusChange();
		},

		manualSiteArea(){
			this.onStatusChange();
		},

		currentStage(){
			this.setCurrentStage();
		},

		// when map coordinates are chosen, the area will be updated
		// dependant values need to be reset
		siteArea(){
			this.$store.commit( 'stages/setCalculatedSiteAreaIsCorrect', true );
			this.$store.commit( 'stages/setManualSiteArea', this.displaySiteArea );
		},
	},

	methods: {

		...mapMutations( [ 'setLoading', 'clearLoading' ] ),

		...mapMutations( 'stages', [
			'setAddress',
			'unsetAddress',
			'setReverseGeocodeAddress',
			'setGeocodeAddress',
		] ),

		setCurrentStage(){
			this.$emit( 'currentStage', this.currentStage );
		},

		onStatusChange(){

			if( this.hasAddress && ( this.calculatedSiteAreaIsCorrect === true || this.manualSiteArea ) ){
				this.$emit( 'input', matchAnswer( this.question, { value: ( this.hasSiteArea && this.calculatedSiteAreaIsCorrect ) ? this.siteArea : this.manualSiteArea, unit: 'SQM' } ) );
			}
			else if( !this.hasAddress && ( this.geoJSONBoundary || this.manualAddressValid ) ){
				// return value for initial step as callback function
				// this enables the transition to the next step
				this.$emit( 'input', async () => {

					this.setLoading();

					if( this.manualAddressValid ) {
						await this.geocode();
					}
					else if( this.geoJSONBoundary ) {
						await this.reverseGeocode();
					}

					this.clearLoading();

					this.hasAddress = true;
					this.hasSiteArea = !!this.siteArea && this.siteArea > 0;
					this.displaySiteAreaUnifiedValue = parseFloat( this.displaySiteAreaUnified.toFixed( 3 ) );
					this.onStatusChange();

				} );
			}
			else{
				this.$emit( 'input', null );
			}
		},

		onAddressError() {
			this.unsetAddress();
		},

		onAddressInput( addressModel ) {
			this.setAddress( addressModel );
		},

		nextClick(){

			return false;
		},

		async geocode() {

			const [ err, response ] = await to(
				get( 'geocode', {
					street: this.manualAddress.addressLines.join( ', ' ),
					city: this.manualAddress.city,
					postcode: this.manualAddress.postcode,
				} )
			);

			if( err ) {
				console.error( err );
				throw err;
			}

			this.setGeocodeAddress( response || {} );
		},

		async reverseGeocode() {

			const centerPoint = getCoord( centroid( this.geoJSONBoundary ) );

			const [ err, response ] = await to(
				get( 'reverse-geocode', {
					lat: centerPoint[ 1 ],
					lon: centerPoint[ 0 ],
				} )
			);

			if( err ) {
				console.error( err );
				throw err;
			}

			this.setReverseGeocodeAddress( response || {} );
		},

		onSiteAreaCorrectInput( bool, inputId ) {

			if( bool ){
				if( inputId === 'yes' ) {
					this.$store.commit( 'stages/setCalculatedSiteAreaIsCorrect', true );
				}
				else {
					this.$store.commit( 'stages/setCalculatedSiteAreaIsCorrect', false );
				}

				this.$store.commit( 'stages/setManualSiteArea', this.displaySiteArea );
			}
		},

		onManualSiteAreaInput( value ) {
			this.$store.commit( 'stages/setManualSiteArea', parseFloat( value ) );
		},

		switchDisplayUnits( prev, current ) {

			const area = convertWithUnits( this.displaySiteAreaUnifiedValue, prev.id, current.id );

			this.displaySiteAreaUnifiedValue = area;

		},

	},
};
</script>

<style lang="scss" scoped>
main {
	margin: 32px 0;
}
</style>
